import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsPoolToken } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { PermitData } from './useApprove';
import { usefromTokens } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';


export default function useWithdraw(approvalState: ButtonState, tokens: BigNumber, invalidInput: boolean, permitData: PermitData): [ButtonState, () => Promise<void>] {
  const onchainInteractionsPoolToken = useOnchainInteractionsPoolToken();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const val = usefromTokens(tokens);
  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const summary = `Withdraw ${formatFloat(val)} ${symbol}`;
  
  const withdrawState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalState != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [invalidInput, approvalState, pending]);

  const withdraw = useCallback(async (): Promise<void> => {
    if (withdrawState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsPoolToken.withdraw(tokens, permitData, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [withdrawState, onchainInteractionsPoolToken, addTransaction, tokens, permitData]);

  return [withdrawState, withdraw];
}